import React, { FC, useCallback, useEffect, useState } from "react";

import uniq from "lodash-es/uniq";
import { useIntl } from "react-intl";

import { RequiredFields } from "@mapmycustomers/shared";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import { knownShapes } from "@mapmycustomers/shared/enum/MarkerShape";
import PinLegendClassificationType from "@mapmycustomers/shared/enum/pinLegends/PinLegendClassificationType";
import PinLegendDatingMethod from "@mapmycustomers/shared/enum/pinLegends/PinLegendDatingMethod";
import { Breakpoints } from "@mapmycustomers/shared/types/map/PinLegend";
import { NumberField } from "@mapmycustomers/ui";

import {
  DEFAULT_BUCKETS_COUNT,
  DEFAULT_NO_VALUE_SHAPE,
} from "@app/scene/map/components/pinLegends/utils/consts";
import { formatDate } from "@app/util/formatters";
import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";

import styles from "./ClassificationEditor.module.scss";
import BreakpointsField from "./components/BreakpointsField";
import DataLevelField from "./components/DataLevelField";
import DatingMethodField from "./components/DatingMethodField";
import {
  ClassificationMethodInfo,
  DataLevelInfo,
  DatingMethodInfo,
} from "./components/infoTooltips";
import MethodField from "./components/MethodField";
import ShapeThemeField from "./components/ShapeThemeField";
import StyleSection from "./components/StyleSection";
import messages from "./messages";
import ClassificationStyleEditorProps from "./types/ClassificationStyleEditorProps";

const MIN_BUCKETS = 3;

export interface ShapesClassificationEditorProps
  extends RequiredFields<ClassificationStyleEditorProps, "defaultShape" | "shapes"> {}

const supportedClassifications = [
  PinLegendClassificationType.MANUAL,
  PinLegendClassificationType.EQUAL_INTERVAL,
  PinLegendClassificationType.QUANTILE,
  PinLegendClassificationType.NATURAL_BREAKS,
];

const ShapesClassificationEditor: FC<ShapesClassificationEditorProps> = ({
  breakpoints,
  classification,
  dataLevel,
  datingMethod,
  defaultShape,
  field,
  onBreakpointsChange,
  onClassificationChange,
  onDataLevelChange,
  onDatingMethodChange,
  onDefaultShapeChange,
  onError,
  onShapeChange,
  shapes,
}) => {
  const intl = useIntl();

  // sync shapes array to match new number of buckets
  const syncShapesWithBuckets = useCallback(
    (numberOfBuckets: number) => {
      // resize shapes array to match new number of buckets
      if (shapes.length > numberOfBuckets) {
        onShapeChange?.(shapes.toSpliced(numberOfBuckets));
      } else if (shapes.length < numberOfBuckets) {
        const result = [...shapes];
        const availableShapes = knownShapes.filter((shape) => shape !== DEFAULT_NO_VALUE_SHAPE);
        while (result.length < numberOfBuckets) {
          result.push({
            shape: availableShapes[Math.floor(Math.random() * availableShapes.length)],
          }); // default shape for items
        }
        onShapeChange?.(result);
      }
    },
    [onShapeChange, shapes]
  );

  const [buckets, setBuckets] = useState<number | undefined>(
    shapes?.length || DEFAULT_BUCKETS_COUNT
  );
  useEffect(() => {
    setBuckets(shapes?.length || DEFAULT_BUCKETS_COUNT);
  }, [shapes?.length]);

  const handleBreakpointsChange = useCallback(
    (breakpoints: Breakpoints) => {
      onBreakpointsChange?.(breakpoints);

      const numberOfBuckets = breakpoints.length ? breakpoints.length + 1 : MIN_BUCKETS;
      setBuckets(numberOfBuckets);
      syncShapesWithBuckets(numberOfBuckets);
    },
    [onBreakpointsChange, syncShapesWithBuckets]
  );

  const handleClassificationChange = useCallback(
    (classification: PinLegendClassificationType) => {
      onClassificationChange?.(classification);
      if (classification === PinLegendClassificationType.MANUAL && breakpoints?.length) {
        setBuckets(breakpoints.length + 1);
        syncShapesWithBuckets(breakpoints.length + 1);
      } else {
        setBuckets(MIN_BUCKETS);
        syncShapesWithBuckets(MIN_BUCKETS);
      }
    },
    [breakpoints?.length, onClassificationChange, syncShapesWithBuckets]
  );

  const handleDatingMethodChange = useCallback(
    (value: PinLegendDatingMethod) => {
      // if switching from absolute to ordinal, let's preserve all values
      // in new format removing duplicates
      // otherwise, remove all breakpoints
      if (
        datingMethod === PinLegendDatingMethod.ABSOLUTE &&
        value === PinLegendDatingMethod.ORDINAL
      ) {
        const updatedBreakpoints = uniq(
          breakpoints
            ?.map((date) => parseApiDate(date))
            .filter(isValidDate)
            .map((date) => formatDate(date, "MM-dd"))
        );
        handleBreakpointsChange(updatedBreakpoints);
      } else {
        handleBreakpointsChange([]);
      }

      onDatingMethodChange?.(value);
    },
    [breakpoints, datingMethod, handleBreakpointsChange, onDatingMethodChange]
  );

  const handleBucketsChange = useCallback(
    (numberOfBuckets: number | undefined) => {
      setBuckets(numberOfBuckets);
      syncShapesWithBuckets(numberOfBuckets ?? MIN_BUCKETS);
    },
    [syncShapesWithBuckets]
  );

  const isDateField = field.type === FieldType.DATE || field.type === FieldType.DATE_TIME;
  let stepIndex = 1;

  return (
    <div className={styles.container}>
      <StyleSection
        info={<DataLevelInfo />}
        title={intl.formatMessage(messages.accessLevelStep, { index: stepIndex++ })}
      >
        <DataLevelField onChange={onDataLevelChange} value={dataLevel} />
      </StyleSection>
      {isDateField && (
        <StyleSection
          info={<DatingMethodInfo />}
          title={intl.formatMessage(messages.datingMethodStep, { index: stepIndex++ })}
        >
          <DatingMethodField onChange={handleDatingMethodChange} value={datingMethod} />
        </StyleSection>
      )}
      <StyleSection
        info={<ClassificationMethodInfo />}
        title={intl.formatMessage(messages.classificationStep, { index: stepIndex++ })}
      >
        <MethodField
          classifications={supportedClassifications}
          onChange={handleClassificationChange}
          value={classification}
        />
      </StyleSection>
      <StyleSection title={intl.formatMessage(messages.bucketsStep, { index: stepIndex++ })}>
        {classification === PinLegendClassificationType.MANUAL ? (
          <BreakpointsField
            breakpoints={breakpoints}
            datingMethod={datingMethod}
            field={field}
            onChange={handleBreakpointsChange}
            onError={onError}
          />
        ) : (
          <NumberField max={9} min={MIN_BUCKETS} onChange={handleBucketsChange} value={buckets} />
        )}
      </StyleSection>
      {!!buckets && (
        <StyleSection title={intl.formatMessage(messages.shapesStep, { index: stepIndex++ })}>
          <ShapeThemeField
            defaultShape={defaultShape}
            onChange={onShapeChange}
            onDefaultShapeChange={onDefaultShapeChange}
            shapes={shapes}
          />
        </StyleSection>
      )}
    </div>
  );
};

export default ShapesClassificationEditor;
